import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CommonPaginationResponse,
  CommonResponse,
  PaginateResponse,
  processNormal,
  processPagination, VoidResponse
} from '../common/response.model';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { ErrorHandlingService } from '../common/error-handling.service';
import { Patient, PatientComment } from './patient.model';
import { PatientForm } from '../../FormModels/patient-form.model';
import { getFullPhoneNumber, getPhoneNumberCountry } from '../../helpers/phone_number.helper';

@Injectable({
  providedIn: 'root'
})
export class PatientsService {

  constructor(private http: HttpClient, private errorHandling: ErrorHandlingService) { }

  /**
   * Obtener el listado de todos los pacientes sin paginacion
   *
   * @return Observable<Patient[]>
   */
  public listAll(): Observable<Patient[]> {
    return this.http.get<CommonResponse<Patient[]>>(
      environment.apiUrl + 'patients',
      {
        params: new HttpParams().append('no-paginate', '1')
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
 * Crear un paciente pre-registrado (Endpoint idéntico al de Flutter)
 */
  public createPrePatient(data: any): Observable<any> {
    return this.http.post<CommonResponse<any>>(
      environment.apiUrl + 'patients/get-token-pre-patient',
      data
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }
  /**
   * Obtener el listado de todos los pacientes con paginacion
   *
   * @return Observable<PaginateResponse<Patient[]>>
   */
  public listPaginated(page, per_page, filter = ''): Observable<PaginateResponse<Patient[]>> {
    let params = new HttpParams().append('paginate_per_page', per_page.toString()).append('page', page);
    if (filter !== '') { params = params.append('filter', filter); }

    return this.http.get<CommonPaginationResponse<Patient[]>>(
      environment.apiUrl + 'patients',
      {
        params
      }
    ).pipe(
      map(resp => processPagination(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Agregar un paciente a la plataforma
   *
   * @return Observable<Patient>
   */
  public add(patient: PatientForm): Observable<Patient> {

    patient.identity_document = patient.identity_document.toString();
    patient.phone_number = getFullPhoneNumber(patient.phone_number_ui);
    patient.phone_number_country = getPhoneNumberCountry(patient.phone_number_ui);

    return this.http.post<CommonResponse<Patient>>(
      environment.apiUrl + 'patients',
      patient
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Ver datos de un paciente
   *
   * @return Observable<Patient>
   */
  public show(id: string): Observable<Patient> {

    return this.http.get<CommonResponse<Patient>>(
      environment.apiUrl + 'patients/' + id
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Ver los comentarios del doctor
   *
   * @return Observable<PatientComment>
   */
  public getCommentsOfDoctor(id: string): Observable<PatientComment> {
    return this.http.get<CommonResponse<Patient>>(
      environment.apiUrl + 'patients/' + id + '/doctor-comments'
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }


  /**
   * Guardar los comentarios del doctor
   *
   * @return Observable<PatientComment>
   */
  public saveCommentsOfDoctor(id: string, value: string): Observable<PatientComment> {
    return this.http.post<CommonResponse<Patient>>(
      environment.apiUrl + 'patients/' + id + '/doctor-comments',
      {
        value
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Editar datos del paciente
   *
   * @return Observable<Patient>
   */
  public edit(id: string, patient: PatientForm): Observable<Patient> {

    patient.identity_document = patient.identity_document.toString();

    return this.http.put<CommonResponse<Patient>>(
      environment.apiUrl + 'patients/' + id,
      {
        name: patient.name
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }

  /**
   * Eliminar datos del paciente
   *
   * @return Observable<VoidResponse>
   */
  delete(id: string, type: string = 'drop'): Observable<VoidResponse> {


    return this.http.delete<CommonResponse<VoidResponse>>(
      environment.apiUrl + 'patients/' + id,
      {
        params: new HttpParams().append('type', type)
      }
    ).pipe(
      map(resp => processNormal(resp)),
      catchError(this.errorHandling.handleErrors)
    );
  }


  /**
   * Ping de actividad del paciente
   *
   * @return Observable<void>
   */
  public pingActivity(): Observable<void> {

    return this.http.get<void>(
      environment.apiUrl + 'ping/patient'
    ).pipe(
      catchError(this.errorHandling.handleErrors)
    );
  }
}
